/**
 * StatCard — flat KPI card (HumanAI mockup style): small inline icon +
 * label, big Poppins number, tone-coloured delta/hint. Optional link.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, type LucideIcon } from 'lucide-react';

type Tone = 'default' | 'warn' | 'danger' | 'success';

interface Props {
  icon: LucideIcon;
  label: string;
  value: string | number;
  hint?: string;
  tone?: Tone;
  to?: string;
}

export function StatCard({ icon: Icon, label, value, hint, tone = 'default', to }: Props) {
  const hintColor =
    tone === 'warn' ? 'text-amber-600'
    : tone === 'danger' ? 'text-red-600'
    : tone === 'success' ? 'text-brand-600'
    : 'text-gray-400';
  const inner = (
    <>
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
        <Icon className="w-4 h-4 text-brand-600 shrink-0" />
        <span className="truncate">{label}</span>
        {to && <ArrowRight className="w-4 h-4 text-gray-300 ml-auto shrink-0" />}
      </div>
      <div className="font-display text-3xl font-semibold tabular-nums text-gray-900 leading-tight">{value}</div>
      {hint && <div className={`font-display text-xs tabular-nums mt-1 ${hintColor}`}>{hint}</div>}
    </>
  );
  return to ? (
    <Link to={to} className="rounded-xl bg-white border border-gray-200 p-4 block hover:border-brand-300 transition">
      {inner}
    </Link>
  ) : (
    <div className="rounded-xl bg-white border border-gray-200 p-4">{inner}</div>
  );
}
